import type { CssDeclaration } from '../styled';
import { css } from '../styled';
import { color, modifier, radius, spacing, typography, variants } from '../theme';

export { withLinkStyle } from './link';
export { default as outline } from './outlineStyle';

export const withInputErrorStyle = css`
    border-color: ${color('red.600')};
`;

export const withInputErrorModifier = modifier('error', withInputErrorStyle);

export const withInputDisabledStyle = css`
    color: ${color('content.disabled')};
    background-color: ${color('surface.secondary')};
    border-color: ${color('border.primary')};

    &:hover,
    [data-dev-state='hover'] & {
        border-color: ${color('border.primary')};
    }

    &:focus,
    [data-dev-state='focus'] & {
        border-color: ${color('border.primary')};
    }

    &::placeholder {
        color: ${color('content.disabled')};
    }
`;

export const withInputDisabledModifier = css`
    ${modifier('disabled', withInputDisabledStyle)}

    &:disabled {
        ${withInputDisabledStyle}
    }
`;

export interface InputStyleProps {
    error?: boolean;
    disabled?: boolean;
}

export const withInputContainerStyle = css<InputStyleProps>`
    box-sizing: border-box;

    ${typography('description')};

    width: 100%;
    color: ${color('content.primary')};
    border: 1px solid ${color('border.primary')};
    border-radius: ${radius('md')};
    transition: border-color ease-in-out 0.15s;
    padding: ${spacing(2, 3)};
    background-color: ${color('background.input')};

    @media (prefers-reduced-motion: reduce) {
        transition: none;
    }

    &:hover,
    [data-dev-state='hover'] & {
        border-color: ${color('primary.300')};
    }

    &:focus-within,
    [data-dev-state='focus'] & {
        border-color: ${color('primary.500')};
    }

    ${withInputErrorModifier}
    ${withInputDisabledModifier}
`;

export const withInputFieldStyle = css<InputStyleProps>`
    box-sizing: content-box;
    -webkit-font-smoothing: subpixel-antialiased;

    ${typography('description')};
    color: ${color('content.primary')};
    background: none;

    &&&:focus,
    &&&:focus-visible,
    [data-dev-state='focus'] &&& {
        outline: none !important;
    }

    &::placeholder {
        ${typography('description')};
        color: ${color('content.secondary')};
    }

    ${withInputErrorModifier}
    ${withInputDisabledModifier}
`;

export const withInputStyle = css<InputStyleProps>`
    ${withInputContainerStyle};
    ${withInputFieldStyle};

    // Unfortunately, we need order of container -> input, but we need certain props from container
    // so we override those here. Currently I don't see way to combine those styles cleaner.
    box-sizing: border-box;
    background: ${color('background.input')};
`;

export type InputSizeVariants = 'small' | 'default';

/**
 * This helper allows us to define custom styles for inputs in a way that allows size names, defaults and ensuring
 * that all required sizes are defined.
 */
export const buildInputSizeStyles = <T extends object>(sizeStyles: Record<InputSizeVariants, CssDeclaration<T>>) =>
    variants('inputSize', sizeStyles, 'default');

export const withInputSizeVariants = buildInputSizeStyles({
    small: css`
        height: 28px;
        min-height: 28px;
        line-height: 28px;
        ${typography('caption1')};

        &::placeholder {
            ${typography('caption1')}
        }

        &::-moz-placeholder {
            line-height: 28px;
        }
    `,
    default: css`
        height: 33px;
        min-height: 33px;
        line-height: 33px;

        &::-moz-placeholder {
            line-height: 33px;
        }
    `,
});

export const oneLineEllipsis = css`
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

export const ratioContentStyle = css`
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    right: 0;
    max-width: 100%;
    max-height: 100%;
    width: 100%;
    height: 100%;
`;
